import { useCallback, useEffect, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import {
  kdsService,
  type KdsOrderView,
  type KdsOrderlineView,
  type KitchenStatusValue,
} from '../../../services/kds/kds.service';
import { subscribeKdsStream, type KdsStreamEvent } from '../../../services/kds/kds-stream.service';
import { printersService, type PrinterView } from '../../../services/printers/printers.service';
import { hardRefresh } from '../../../utils/hard-refresh';
import {
  playKdsNotificationSound,
  primeKdsNotificationSound,
  type KdsNotificationSound,
} from '../audio/kds-notification-sound';
import { kdsI18n, type KdsLanguage } from '../i18n/kds-i18n';
import {
  sortOrders,
  buildTabCards,
  computeFireWaveKeys,
  buildOverviewGroups,
  collectKdsCategories,
  lineMatchesCategory,
  type KdsCategoryFilter,
  matchesSearch,
  readFavorites,
  buildKdsPrintPreview,
  buildKdsPrintPreviewFromBackend,
  isLineCooking,
  isLineCancelled,
  splitLineIds,
  type KdsLine,
  captureKdsReceiptSnapshot,
  favoritesKey,
  DARK_MODE_KEY,
  KDS_NOTIFICATION_SOUND_KEY,
  KDS_ZOOM_KEY,
  KDS_ORDER_SORT_KEY,
  KDS_ORDER_TYPE_FILTER_KEY,
  KDS_LANGUAGE_KEY,
  KDS_SHOW_DONE_TAB_KEY,
  KDS_MERGE_ORDERS_KEY,
  kdsKitchenPrinterKey,
  type KdsOrderTypeFilter,
  type KdsTab,
  type KdsPrintPreview,
  type KdsOrderSort,
} from '../utils/kds-page';
import { useEmployeeSession } from '../../../contexts/EmployeeSessionProvider';

// sessionStorage key for the post-reload toast; a hard reload destroys any pre-reload notification.
const KDS_REFRESH_NOTICE_KEY = 'kds.refreshNotice';

// Summary: All state, refs, effects, and handler logic for the KDS screen (Rule 32).
export function useKdsController(tenantId: string) {
  const { t } = useTranslation(undefined, { i18n: kdsI18n });
  const { employeeState, hydrateEmployeeState } = useEmployeeSession();

  const [orders, setOrders] = useState<KdsOrderView[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<KdsTab>('COOKING');
  const [isDark, setIsDark] = useState<boolean>(
    () => localStorage.getItem(DARK_MODE_KEY) === 'true',
  );
  const [favorites, setFavorites] = useState<Set<number>>(() => readFavorites(tenantId));
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<KdsCategoryFilter>('all');
  const [isOverviewOpen, setIsOverviewOpen] = useState(true);
  const updatingRef = useRef<Set<number>>(new Set());
  const ordersRef = useRef<KdsOrderView[]>([]);
  const fireWaveKeysRef = useRef<Map<number, string>>(new Map());
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [language, setLanguage] = useState<KdsLanguage>(() => {
    const raw = localStorage.getItem(KDS_LANGUAGE_KEY);
    return raw === 'en' ? 'en' : 'he';
  });
  const [zoom, setZoom] = useState<number>(() => {
    const raw = localStorage.getItem(KDS_ZOOM_KEY);
    const parsed = raw ? parseFloat(raw) : 1;
    return isNaN(parsed) ? 1 : Math.min(2, Math.max(0.5, parsed));
  });
  const gridRef = useRef<HTMLElement>(null);
  useEffect(() => {
    gridRef.current?.style.setProperty('--kds-card-zoom', String(zoom));
  }, [zoom]);
  const [orderSort, setOrderSort] = useState<KdsOrderSort>(() => {
    const raw = localStorage.getItem(KDS_ORDER_SORT_KEY);
    return (raw === 'duration' || raw === 'product_count' || raw === 'table') ? raw : 'duration';
  });
  const [orderTypeFilter, setOrderTypeFilter] = useState<KdsOrderTypeFilter>(() => {
    const raw = localStorage.getItem(KDS_ORDER_TYPE_FILTER_KEY);
    return raw === 'dine_in' || raw === 'take_away' ? raw : 'all';
  });
  const [notificationSound, setNotificationSound] = useState<KdsNotificationSound>(() => {
    const raw = localStorage.getItem(KDS_NOTIFICATION_SOUND_KEY);
    if (raw === 'none' || raw === 'test_message') return raw;
    if (raw === 'ding') return 'test_message';
    return 'test_message';
  });
  const [isSoundUnlockPromptVisible, setIsSoundUnlockPromptVisible] = useState(false);
  const notificationSoundPrimedRef = useRef(false);
  const notificationSoundPrimeInFlightRef = useRef(false);
  const [kitchenPrinterId, setKitchenPrinterId] = useState<number | null>(() => {
    const raw = localStorage.getItem(kdsKitchenPrinterKey(tenantId));
    const parsed = raw ? parseInt(raw, 10) : null;
    return parsed && !isNaN(parsed) ? parsed : null;
  });
  const [showDoneTab, setShowDoneTab] = useState<boolean>(
    () => localStorage.getItem(KDS_SHOW_DONE_TAB_KEY) !== 'false',
  );
  const [mergeOrders, setMergeOrders] = useState<boolean>(
    () => localStorage.getItem(KDS_MERGE_ORDERS_KEY) === 'true',
  );
  const [settingsPrinters, setSettingsPrinters] = useState<PrinterView[]>([]);
  const [settingsPrintersLoading, setSettingsPrintersLoading] = useState(false);
  const [printPreview, setPrintPreview] = useState<KdsPrintPreview | null>(null);
  const [printPreviewImage, setPrintPreviewImage] = useState<string | null>(null);
  const [isQueueingPrint, setIsQueueingPrint] = useState(false);
  const [printPreviewError, setPrintPreviewError] = useState<string | null>(null);
  const printReceiptPaperRef = useRef<HTMLDivElement>(null);
  const printPreviewModalRef = useRef<HTMLElement>(null);
  const printTicketWrapperRef = useRef<HTMLDivElement>(null);
  const printPreviewActionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!printPreview) {
      setPrintPreviewImage(null);
      return;
    }
    let cancelled = false;
    void captureKdsReceiptSnapshot(printReceiptPaperRef.current).then((url) => {
      if (cancelled) return;
      setPrintPreviewImage(url ?? null);
    });
    return () => { cancelled = true; };
  }, [printPreview]);

  useEffect(() => {
    void kdsI18n.changeLanguage(language);
  }, [language]);

  // Prime notification sound once after first user gesture.
  const tryPrimeNotificationSound = useCallback((sound: KdsNotificationSound = notificationSound) => {
    if (sound === 'none') {
      setIsSoundUnlockPromptVisible(false);
      return;
    }
    if (notificationSoundPrimedRef.current || notificationSoundPrimeInFlightRef.current) {
      return;
    }
    notificationSoundPrimeInFlightRef.current = true;
    void primeKdsNotificationSound(sound)
      .then((ok) => {
        if (ok) {
          notificationSoundPrimedRef.current = true;
          setIsSoundUnlockPromptVisible(false);
        } else {
          setIsSoundUnlockPromptVisible(true);
        }
      })
      .finally(() => {
        notificationSoundPrimeInFlightRef.current = false;
      });
  }, [notificationSound]);

  const playNotificationWithFallback = useCallback(async () => {
    if (notificationSound === 'none') {
      setIsSoundUnlockPromptVisible(false);
      return;
    }
    const played = await playKdsNotificationSound(notificationSound);
    if (played) {
      setIsSoundUnlockPromptVisible(false);
      return;
    }
    setIsSoundUnlockPromptVisible(true);
  }, [notificationSound]);

  useEffect(() => {
    const onFirstGesture = () => {
      tryPrimeNotificationSound();
    };
    document.addEventListener('pointerdown', onFirstGesture, { passive: true });
    document.addEventListener('touchstart', onFirstGesture, { passive: true });
    document.addEventListener('keydown', onFirstGesture);
    return () => {
      document.removeEventListener('pointerdown', onFirstGesture);
      document.removeEventListener('touchstart', onFirstGesture);
      document.removeEventListener('keydown', onFirstGesture);
    };
  }, [tryPrimeNotificationSound]);

  const employeeSession =
    employeeState?.employee?.tenantId === tenantId ? employeeState.employeeSession : null;
  // JWT-only mode: no employee session; drives stream/data wiring, writes still allowed.
  const isViewer = !employeeSession;

  const resolvePrinterName = useCallback(
    async (printerId: number | null): Promise<string> => {
      if (!printerId) return t('print.noPrinterSelected');
      const cached = settingsPrinters.find((p) => p.id === printerId);
      if (cached) return cached.name;
      if (!employeeSession) return `Printer #${printerId}`;
      try {
        const loaded = await printersService.listActive(tenantId, employeeSession);
        setSettingsPrinters(loaded);
        return loaded.find((p) => p.id === printerId)?.name ?? `Printer #${printerId}`;
      } catch {
        return `Printer #${printerId}`;
      }
    },
    [employeeSession, settingsPrinters, tenantId, t],
  );

  // Prefer backend preview artifact when printer + session are available.
  const handleOpenPrintPreview = useCallback(
    async (order: KdsOrderView, orderTab: KdsTab) => {
      setPrintPreviewError(null);
      const previewOrderlines = orderTab === 'COOKING' ? order.orderlines.filter(isLineCooking) : order.orderlines;
      const previewOrder: KdsOrderView = previewOrderlines === order.orderlines
        ? order
        : { ...order, orderlines: previewOrderlines };
      const previewOrderlineIds = Array.from(new Set(
        previewOrderlines.map((line) => (line as KdsLine).parentOrderlineId ?? line.id),
      ));
      if (kitchenPrinterId) {
        try {
          const backendPreview = await kdsService.createOrderPrintPreview(
            tenantId,
            order.id,
            kitchenPrinterId,
            employeeSession ?? undefined,
            previewOrderlineIds,
          );
          setPrintPreview(buildKdsPrintPreviewFromBackend(backendPreview.previewId, backendPreview.payload, order));
          return;
        } catch (error) {
          setPrintPreviewError(error instanceof Error ? error.message : t('print.queueFailed'));
        }
      }
      const printerName = await resolvePrinterName(kitchenPrinterId);
      setPrintPreview(buildKdsPrintPreview(previewOrder, printerName, new Date()));
    },
    [employeeSession, kitchenPrinterId, resolvePrinterName, tenantId, t],
  );

  // Close preview and cancel backend artifact when present.
  const handleClosePrintPreview = useCallback(() => {
    if (printPreview?.previewId) {
      void kdsService.cancelOrderPrintPreview(tenantId, printPreview.orderId, printPreview.previewId, employeeSession ?? undefined);
    }
    setPrintPreviewError(null);
    setPrintPreview(null);
  }, [employeeSession, printPreview, tenantId]);

  // Queue confirmed print preview to backend pipeline.
  const handleConfirmPrint = useCallback(async () => {
    if (!printPreview) return;
    if (!kitchenPrinterId) {
      setPrintPreviewError(t('print.selectPrinterBeforePrint'));
      return;
    }
    setIsQueueingPrint(true);
    setPrintPreviewError(null);
    try {
      await kdsService.queueOrderPrint(
        tenantId,
        printPreview.orderId,
        kitchenPrinterId,
        employeeSession ?? undefined,
        printPreview.previewId ?? undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        printPreviewImage ?? undefined,
      );
      setPrintPreview(null);
    } catch (error) {
      setPrintPreviewError(error instanceof Error ? error.message : t('print.queueFailed'));
    } finally {
      setIsQueueingPrint(false);
    }
  }, [printPreview, printPreviewImage, employeeSession, kitchenPrinterId, tenantId, t]);

  useEffect(() => {
    hydrateEmployeeState(tenantId);
  }, [tenantId, hydrateEmployeeState]);

  const hasLoadedOrdersOnceRef = useRef(false);

  useEffect(() => {
    if (!employeeSession && !isViewer) return;
    let cancelled = false;
    hasLoadedOrdersOnceRef.current = false;
    setIsLoading(true);
    setLoadError(null);
    kdsService
      .listOrders(tenantId, employeeSession ?? undefined)
      .then((data) => {
        if (!cancelled) {
          setOrders(data);
          // Seed the ref synchronously so SSE events racing this fetch's commit
          // don't see a stale/empty snapshot and mistake existing orders for new ones.
          ordersRef.current = data;
          hasLoadedOrdersOnceRef.current = true;
          setIsLoading(false);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) { setLoadError(err.message); setIsLoading(false); }
      });
    return () => { cancelled = true; };
  }, [tenantId, employeeSession, isViewer]);

  // Shows the "screen refreshed" toast that survives the hard reload triggered below.
  useEffect(() => {
    let notice: string | null = null;
    try {
      notice = sessionStorage.getItem(KDS_REFRESH_NOTICE_KEY);
      if (notice) {
        sessionStorage.removeItem(KDS_REFRESH_NOTICE_KEY);
      }
    } catch {
      // sessionStorage may be unavailable; skip the post-reload notice in that case.
    }
    if (notice) {
      notifications.show({ message: t('notifications.screenRefreshed'), autoClose: 3000 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    ordersRef.current = orders;
  }, [orders]);

  const playNotificationRef = useRef(playNotificationWithFallback);
  useEffect(() => { playNotificationRef.current = playNotificationWithFallback; }, [playNotificationWithFallback]);
  const tRef = useRef(t);
  useEffect(() => { tRef.current = t; }, [t]);

  useEffect(() => {
    if (!employeeSession && !isViewer) return;
    return subscribeKdsStream(tenantId, employeeSession ?? undefined, (event: KdsStreamEvent) => {
      switch (event.type) {
        case 'kds.order.opened': {
          const order = event.order;
          if (!order.orderlines.length) break;
          const existing = ordersRef.current.find((o) => o.id === order.id);
          const isExisting = Boolean(existing);
          const existingOrderlines = existing?.orderlines ?? [];
          const hasNewLinesForExisting =
            isExisting &&
            order.orderlines.some(
              (incomingLine: KdsOrderlineView) => !existingOrderlines.some((existingLine: KdsOrderlineView) => existingLine.id === incomingLine.id),
            );
          const shouldNotifyNew = hasLoadedOrdersOnceRef.current && (!isExisting || hasNewLinesForExisting);
          setOrders((prev) => {
            const existsInPrev = prev.some((o) => o.id === order.id);
            return existsInPrev
              ? prev.map((o) => (o.id === order.id ? order : o))
              : [order, ...prev];
          });
          if (shouldNotifyNew) {
            void playNotificationRef.current();
            notifications.show({ message: tRef.current('notifications.newOrderline'), autoClose: 3000 });
          }
          break;
        }
        case 'kds.order.closed':
          setOrders((prev) => prev.map((o) => o.id === event.orderId ? { ...o, status: 'closed' } : o));
          break;
        case 'kds.session.closed':
          setOrders((prev) => prev.filter((o) => o.sessionId !== event.sessionId));
          break;
        case 'kds.order.vip_changed':
          setOrders((prev) => prev.map((o) => o.id === event.orderId ? { ...o, isVip: event.isVip } : o));
          break;
        case 'kds.orderline.added': {
          const line = event.orderline;
          const existingOrder = ordersRef.current.find((o) => o.id === event.orderId);
          const isNewLineForExistingOrder = hasLoadedOrdersOnceRef.current && existingOrder
            ? !existingOrder.orderlines.some((existingLine) => existingLine.id === line.id)
            : false;
          setOrders((prev) => {
            return prev.map((o) => {
              if (o.id !== event.orderId || o.orderlines.some((l) => l.id === line.id)) return o;
              return { ...o, orderlines: [...o.orderlines, line] };
            });
          });
          if (isNewLineForExistingOrder) {
            void playNotificationRef.current();
            notifications.show({ message: tRef.current('notifications.newOrderline'), autoClose: 3000 });
          }
          break;
        }
        case 'kds.orderline.status_changed':
          setOrders((prev) =>
            prev.map((o) =>
              o.id !== event.orderId
                ? o
                : {
                    ...o,
                    orderlines: o.orderlines.map((l) => {
                      if (l.id !== event.orderlineId) return l;
                      // The backend now sends the authoritative wasPulledBack (derived from the
                      // kds_audit_trail event_type, e.g. distinguishing a real manual pull-back
                      // from a reprint that also resets READY/DONE -> COOKING). Only guess from
                      // the transition shape as a fallback for older/unspecified events.
                      const wasReadyOrDone = l.kitchenStatus === 'READY' || l.kitchenStatus === 'DONE';
                      const wasPulledBack = event.wasPulledBack ?? (event.kitchenStatus === 'COOKING' && wasReadyOrDone);
                      return {
                        ...l,
                        kitchenStatus: event.kitchenStatus,
                        status: event.cancelled ? 'canceled' : l.status,
                        wasPulledBack,
                        // Prefer the backend's real, DB-committed updated_at (shared across a
                        // whole batch update, e.g. every line in one reprint) over a fresh
                        // client clock read — otherwise each event in the same batch gets its
                        // own timestamp and the fire-wave grouping splits them into separate
                        // tickets on the FIRST live render (only merging back after a refresh
                        // re-fetches the real, identical DB timestamps).
                        updatedAt: event.updatedAt ?? new Date().toISOString(),
                      };
                    }),
                  },
            ),
          );
          break;
        case 'kds.selection.status_changed':
          if (event.kitchenStatus === 'HIDE') {
            setOrders((prev) =>
              prev.map((o) =>
                o.id !== event.orderId
                  ? o
                  : {
                      ...o,
                      orderlines: o.orderlines.map((ol) =>
                        ol.id !== event.orderlineId
                          ? ol
                          : {
                              ...ol,
                              selections: (ol.selections ?? []).map((s) =>
                                s.id !== event.selectionId
                                  ? s
                                  : { ...s, kitchenStatus: 'HIDE', updatedAt: new Date().toISOString() },
                              ),
                            },
                      ),
                    },
              ),
            );
          } else {
            setOrders((prev) =>
              prev.map((o) =>
                o.id !== event.orderId
                  ? o
                  : {
                      ...o,
                      orderlines: o.orderlines.map((ol) =>
                        ol.id !== event.orderlineId
                          ? ol
                          : {
                              ...ol,
                              selections: (ol.selections ?? []).map((s) => {
                                if (s.id !== event.selectionId) return s;
                                const wasReadyOrDone = s.kitchenStatus === 'READY' || s.kitchenStatus === 'DONE';
                                const wasPulledBack = event.wasPulledBack ?? (event.kitchenStatus === 'COOKING' && wasReadyOrDone);
                                return { ...s, kitchenStatus: event.kitchenStatus, wasPulledBack, updatedAt: new Date().toISOString() };
                              }),
                            },
                      ),
                    },
              ),
            );
          }
          break;
        case 'kds.orderline.note_changed':
          setOrders((prev) =>
            prev.map((o) =>
              o.id !== event.orderId
                ? o
                : {
                    ...o,
                    orderlines: o.orderlines.map((l) =>
                      l.id !== event.orderlineId
                        ? l
                        : { ...l, note: event.note, updatedAt: new Date().toISOString() },
                    ),
                  },
            ),
          );
          break;
        case 'kds.refresh-requested':
          try {
            sessionStorage.setItem(KDS_REFRESH_NOTICE_KEY, String(Date.now()));
          } catch {
            // sessionStorage unavailable: worst case the reload just skips the toast.
          }
          void hardRefresh();
          break;
      }
    });
  }, [tenantId, employeeSession, isViewer]);

  useEffect(() => {
    if (!isSettingsOpen || !employeeSession) return;
    let cancelled = false;
    setSettingsPrintersLoading(true);
    printersService
      .listActive(tenantId, employeeSession)
      .then((data) => { if (!cancelled) { setSettingsPrinters(data); setSettingsPrintersLoading(false); } })
      .catch(() => { if (!cancelled) { setSettingsPrintersLoading(false); } });
    return () => { cancelled = true; };
  }, [isSettingsOpen, tenantId, employeeSession]);

  const handleToggleDark = useCallback(() => {
    setIsDark((prev) => {
      const next = !prev;
      localStorage.setItem(DARK_MODE_KEY, String(next));
      return next;
    });
  }, []);

  const handleToggleFavorite = useCallback(
    (orderId: number) => {
      setFavorites((prev) => {
        const next = new Set<number>(prev);
        if (next.has(orderId)) { next.delete(orderId); } else { next.add(orderId); }
        localStorage.setItem(favoritesKey(tenantId), JSON.stringify(Array.from(next)));
        return next;
      });
    },
    [tenantId],
  );

  const handleStatusTap = useCallback(
    async (line: KdsLine, forcedNext?: KitchenStatusValue) => {
      if (updatingRef.current.has(line.id) || isLineCancelled(line)) return;
      updatingRef.current.add(line.id);
      try {
        const current = line.kitchenStatus;
        const nextStatus = forcedNext ?? (
          current === null || current === 'COOKING'
            ? 'READY'
            : current === 'READY'
              ? 'COOKING'
              : 'READY'
        );
        if (line.lineKind === 'selection' && line.selectionId != null) {
          await kdsService.batchUpdateSelectionKitchenStatus(tenantId, [line.selectionId], nextStatus, employeeSession ?? undefined);
        } else {
          await kdsService.batchUpdateKitchenStatus(tenantId, [line.parentOrderlineId], nextStatus, employeeSession ?? undefined);
        }
      } finally {
        updatingRef.current.delete(line.id);
      }
    },
    [tenantId, employeeSession],
  );

  const handleSearchClose = useCallback(() => {
    setSearchQuery('');
    setIsSearchOpen(false);
  }, []);

  const bulkSetStatus = useCallback(
    async (order: KdsOrderView, targetStatus: KitchenStatusValue, filter: (l: KdsLine) => boolean) => {
      // `order` is always a ticket card's displayOrder here, whose .orderlines are already
      // expanded KdsLines (each combo selection already its own entry with selections: []).
      // Re-running expandOrderLines on them would treat each combo selection-line as a fresh
      // non-combo orderline (since selections is empty) and clobber its real
      // parentOrderlineId/selectionId with the selection's own id — read them as KdsLines
      // directly instead of re-expanding.
      const lines = (order.orderlines as KdsLine[]).filter(filter).filter((l) => !updatingRef.current.has(l.id));
      if (lines.length === 0) return;
      const { orderlineIds, selectionIds } = splitLineIds(lines);
      const orderlineIdSet = new Set(orderlineIds);
      const selectionIdSet = new Set(selectionIds);
      setOrders((prev) =>
        prev.map((o) => (o.id !== order.id ? o : applyStatusToOrder(o, orderlineIdSet, selectionIdSet, targetStatus))),
      );
      lines.forEach((l) => updatingRef.current.add(l.id));
      try {
        const calls: Promise<void>[] = [];
        if (orderlineIds.length > 0) {
          calls.push(kdsService.batchUpdateKitchenStatus(tenantId, orderlineIds, targetStatus, employeeSession ?? undefined));
        }
        if (selectionIds.length > 0) {
          calls.push(kdsService.batchUpdateSelectionKitchenStatus(tenantId, selectionIds, targetStatus, employeeSession ?? undefined));
        }
        await Promise.all(calls);
      } finally {
        lines.forEach((l) => updatingRef.current.delete(l.id));
      }
    },
    [tenantId, employeeSession],
  );

  const handleMarkAllReady = useCallback(
    (order: KdsOrderView) =>
      bulkSetStatus(order, 'READY', (l) => l.kitchenStatus === null || l.kitchenStatus === 'COOKING'),
    [bulkSetStatus],
  );
  const handleMarkAllCooking = useCallback(
    (order: KdsOrderView) => bulkSetStatus(order, 'COOKING', (l) => l.kitchenStatus === 'READY'),
    [bulkSetStatus],
  );
  const handleMarkAllDone = useCallback(
    (order: KdsOrderView) => bulkSetStatus(order, 'DONE', (l) => l.kitchenStatus === 'READY'),
    [bulkSetStatus],
  );
  const handleMarkAllReadyFromDone = useCallback(
    (order: KdsOrderView) => bulkSetStatus(order, 'READY', (l) => l.kitchenStatus === 'DONE'),
    [bulkSetStatus],
  );

  const handleZoomChange = useCallback((v: number) => {
    setZoom(v);
    localStorage.setItem(KDS_ZOOM_KEY, String(v));
  }, []);

  const handleLanguageChange = useCallback((next: KdsLanguage) => {
    setLanguage(next);
    localStorage.setItem(KDS_LANGUAGE_KEY, next);
  }, []);

  const handleOrderSortChange = useCallback((v: KdsOrderSort) => {
    setOrderSort(v);
    localStorage.setItem(KDS_ORDER_SORT_KEY, v);
  }, []);

  const handleOrderTypeFilterChange = useCallback((v: KdsOrderTypeFilter) => {
    setOrderTypeFilter(v);
    localStorage.setItem(KDS_ORDER_TYPE_FILTER_KEY, v);
  }, []);

  const handleNotificationSoundChange = useCallback((v: KdsNotificationSound) => {
    setNotificationSound(v);
    localStorage.setItem(KDS_NOTIFICATION_SOUND_KEY, v);
    tryPrimeNotificationSound(v);
  }, [tryPrimeNotificationSound]);

  const handleShowDoneTabChange = useCallback((v: boolean) => {
    setShowDoneTab(v);
    localStorage.setItem(KDS_SHOW_DONE_TAB_KEY, String(v));
    if (!v) setActiveTab((prev) => (prev === 'DONE' ? 'COOKING' : prev));
  }, []);

  const handleMergeOrdersChange = useCallback((v: boolean) => {
    setMergeOrders(v);
    localStorage.setItem(KDS_MERGE_ORDERS_KEY, String(v));
  }, []);

  const handleKitchenPrinterChange = useCallback((v: number | null) => {
    setKitchenPrinterId(v);
    if (v !== null) {
      localStorage.setItem(kdsKitchenPrinterKey(tenantId), String(v));
    } else {
      localStorage.removeItem(kdsKitchenPrinterKey(tenantId));
    }
  }, [tenantId]);

  // Hide updates use the same batch-status API and local optimistic patch.
  const handleHide = useCallback((orderId: number, lines: KdsLine[], _tab: KdsTab) => {
    if (lines.length === 0) return;
    const { orderlineIds, selectionIds } = splitLineIds(lines);
    const orderlineIdSet = new Set(orderlineIds);
    const selectionIdSet = new Set(selectionIds);
    setOrders((prev) =>
      prev.map((o) =>
        o.id !== orderId
          ? o
          : {
              ...o,
              orderlines: o.orderlines.map((ol) => {
                const updatedOl = orderlineIdSet.has(ol.id)
                  ? { ...ol, kitchenStatus: 'HIDE' as const, updatedAt: new Date().toISOString() }
                  : ol;
                return updatedOl.selections && updatedOl.selections.length > 0
                  ? {
                      ...updatedOl,
                      selections: updatedOl.selections.map((s) =>
                        selectionIdSet.has(s.id)
                          ? { ...s, kitchenStatus: 'HIDE', updatedAt: new Date().toISOString() }
                          : s,
                      ),
                    }
                  : updatedOl;
              }),
            },
      ),
    );
    if (orderlineIds.length > 0) {
      void kdsService.batchUpdateKitchenStatus(tenantId, orderlineIds, 'HIDE', employeeSession ?? undefined);
    }
    if (selectionIds.length > 0) {
      void kdsService.batchUpdateSelectionKitchenStatus(tenantId, selectionIds, 'HIDE', employeeSession ?? undefined);
    }
  }, [tenantId, employeeSession]);

  fireWaveKeysRef.current = computeFireWaveKeys(orders, fireWaveKeysRef.current);
  const tabCards = buildTabCards(orders, mergeOrders, fireWaveKeysRef.current);

  const typeFilteredTabCards =
    orderTypeFilter === 'all'
      ? tabCards[activeTab]
      : tabCards[activeTab].filter((card) => (
          card.displayOrder.orderType === orderTypeFilter
        ));

  const filteredTabCards = searchQuery
    ? typeFilteredTabCards.filter((card) => matchesSearch(card.displayOrder, searchQuery))
    : typeFilteredTabCards;

  // Chips list from the search-filtered cards so every present category stays selectable.
  const { categories: kdsCategories, hasUncategorized } = collectKdsCategories(filteredTabCards);

  // Category chip filter: keep only matching lines and drop cards left empty.
  const categoryFilteredCards =
    categoryFilter === 'all'
      ? filteredTabCards
      : filteredTabCards
          .map((card) => ({
            ...card,
            displayOrder: {
              ...card.displayOrder,
              orderlines: card.displayOrder.orderlines.filter((line) => lineMatchesCategory(line, categoryFilter)),
            },
          }))
          .filter((card) => card.displayOrder.orderlines.length > 0);

  const overviewGroups = buildOverviewGroups(categoryFilteredCards, activeTab);

  const sortedOrderIds = sortOrders(
    categoryFilteredCards.map((card) => card.displayOrder),
    orderSort,
  ).map((o) => o.id);
  const orderIdRank = new Map(sortedOrderIds.map((id, idx) => [id, idx] as const));

  const sortedTabCards = [...categoryFilteredCards]
    .sort((a, b) => {
      const rankDiff = (orderIdRank.get(a.displayOrder.id) ?? 0) - (orderIdRank.get(b.displayOrder.id) ?? 0);
      if (rankDiff !== 0) return rankDiff;
      return a.cardId.localeCompare(b.cardId);
    })
    .sort((a, b) => Number(favorites.has(b.sourceOrder.id)) - Number(favorites.has(a.sourceOrder.id)));

  return {
    employeeSession,
    isViewer,
    isLoading,
    loadError,
    activeTab,
    setActiveTab,
    tabCards,
    sortedTabCards,
    overviewGroups,
    kdsCategories,
    hasUncategorized,
    categoryFilter,
    setCategoryFilter,
    isDark,
    favorites,
    isSearchOpen,
    setIsSearchOpen,
    searchQuery,
    setSearchQuery,
    isOverviewOpen,
    setIsOverviewOpen,
    isSettingsOpen,
    setIsSettingsOpen,
    isSoundUnlockPromptVisible,
    language,
    handleLanguageChange,
    zoom,
    handleZoomChange,
    orderSort,
    handleOrderSortChange,
    orderTypeFilter,
    handleOrderTypeFilterChange,
    notificationSound,
    handleNotificationSoundChange,
    kitchenPrinterId,
    handleKitchenPrinterChange,
    showDoneTab,
    handleShowDoneTabChange,
    mergeOrders,
    handleMergeOrdersChange,
    settingsPrinters,
    settingsPrintersLoading,
    printPreview,
    isQueueingPrint,
    printPreviewError,
    gridRef,
    printReceiptPaperRef,
    printPreviewModalRef,
    printTicketWrapperRef,
    printPreviewActionsRef,
    tryPrimeNotificationSound,
    handleToggleDark,
    handleToggleFavorite,
    handleStatusTap,
    handleSearchClose,
    handleMarkAllReady,
    handleMarkAllCooking,
    handleMarkAllDone,
    handleMarkAllReadyFromDone,
    handleHide,
    handleOpenPrintPreview,
    handleClosePrintPreview,
    handleConfirmPrint,
  };
}

function applyStatusToOrder(
  order: KdsOrderView,
  orderlineIds: Set<number>,
  selectionIds: Set<number>,
  status: KitchenStatusValue,
): KdsOrderView {
  return {
    ...order,
    orderlines: order.orderlines.map((ol) => {
      const base = orderlineIds.has(ol.id) ? { ...ol, kitchenStatus: status } : ol;
      if (!base.selections || base.selections.length === 0) return base;
      if (!base.selections.some((s) => selectionIds.has(s.id))) return base;
      return {
        ...base,
        selections: base.selections.map((s) => (selectionIds.has(s.id) ? { ...s, kitchenStatus: status } : s)),
      };
    }),
  };
}
